import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axiosInstance from "../../../axiosInstance";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import IconButton from "../../../components/IconButton/IconButton";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import GeneralTable from "../../../components/Table/GeneralTable";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import { toast } from "react-toastify";
import "./Warehouse.scss";
import { useAuth } from "../../../contexts/AuthContext";
import AccessDenied from "../../AccessDenied";

const Warehouse = ({ markPageAsCompleted }) => {
  const [editableWarehouses, setEditableWarehouses] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [bulkUpdateEnabled, setBulkUpdateEnabled] = useState(false);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [initialValues, setInitialValues] = useState({
    id: null,
    name: "",
    location: "",
    capacity: "",
    status: "ENABLED",
  });
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [popupAction, setPopupAction] = useState(() => () => {});
  const { user } = useAuth();

  const SIDEBAR_COLLAPSED_KEY = "warehouse_sidebar_collapsed";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  const INFO_CARD_COLLAPSED_KEY = "warehouse_info_card_collapsed";
  const [isInfoCardCollapsed, setIsInfoCardCollapsed] = useState(() => {
    try {
      return localStorage.getItem(INFO_CARD_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggleInfoCard = () => {
    setIsInfoCardCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(INFO_CARD_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  // Derived, real-data metric stats (no fabricated numbers)
  const warehouseStats = useMemo(() => {
    const total = warehouses.length;
    const active = warehouses.filter((w) => w.status === "ENABLED").length;
    const disabled = warehouses.filter((w) => w.status === "DISABLED").length;
    const totalCapacity = warehouses.reduce(
      (sum, w) => sum + (parseInt(w.capacity) || 0),
      0,
    );
    return { total, active, disabled, totalCapacity };
  }, [warehouses]);

  const allowedRoles = [
    "ROLE_INVENTORY-MANAGER",
    "ROLE_ERP-ADMIN",
    "ROLE_CRM-ADMIN",
  ];

  const roleApiMap = {
    "ROLE_INVENTORY-MANAGER": process.env.REACT_APP_API_INV_MANAGER_URL,
    "ROLE_ERP-ADMIN": process.env.REACT_APP_API_ERP_ADMIN_URL,
    "ROLE_CRM-ADMIN": process.env.REACT_APP_API_CRM_ADMIN_URL,
  };

  const userRole = user?.authorities?.find((role) =>
    allowedRoles.includes(role),
  );
  const API_BASE = userRole ? roleApiMap[userRole] : null;

  const fetchWarehouses = useCallback(async () => {
    if (!API_BASE) return;

    setLoading(true);
    try {
      const res = await axiosInstance.get(`${API_BASE}/warehouses`);
      const data = (res.data?.data || []).map((warehouse) => ({
        ...warehouse,
        status: warehouse.status?.toUpperCase() || "DISABLED",
      }));
      setWarehouses(data);
      setEditableWarehouses(data.map((w) => ({ ...w })));

      if (data.length > 0) {
        markPageAsCompleted("Warehouse");
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, markPageAsCompleted]); // ✅ Added here

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  // Validation schema
  const warehouseValidationSchema = Yup.object().shape({
    name: Yup.string().required("Warehouse Name is required"),
    location: Yup.string().required("Location is required"),
    capacity: Yup.number()
      .typeError("Capacity must be a number")
      .required("Capacity is required")
      .positive("Capacity must be positive")
      .integer("Capacity must be an integer"),
  });

  const handleSelectAll = () => {
    const allWarehouseIds = warehouses.map((warehouse) => warehouse.id);
    if (selectAll) {
      setSelectedWarehouses([]);
      setSelectAll(false);
    } else {
      setSelectedWarehouses(allWarehouseIds);
      setSelectAll(true);
    }
  };

  const handleSelectWarehouse = (warehouseId) => {
    setSelectedWarehouses((prev) => {
      if (prev.includes(warehouseId)) {
        return prev.filter((id) => id !== warehouseId);
      } else {
        return [...prev, warehouseId];
      }
    });
  };

  useEffect(() => {
    const allWarehouseIds = warehouses.map((warehouse) => warehouse.id);
    const allSelected =
      allWarehouseIds.length > 0 &&
      allWarehouseIds.every((id) => selectedWarehouses.includes(id));
    setSelectAll(allSelected);
  }, [selectedWarehouses, warehouses]);

  const handleInlineEdit = (id, field, value) => {
    setEditableWarehouses((prev) =>
      prev.map((w) =>
        w.id === id
          ? {
              ...w,
              [field]:
                field === "capacity"
                  ? value === ""
                    ? ""
                    : parseInt(value) || 0
                  : value,
            }
          : w,
      ),
    );
  };

  const handleBulkUpdateClick = () => {
    if (bulkUpdateEnabled) {
      // Validate before submitting
      const hasEmptyFields = editableWarehouses.some(
        (wh) =>
          !wh.name || !wh.location || wh.capacity === "" || wh.capacity <= 0,
      );

      if (hasEmptyFields) {
        toast.error(
          "All fields must be properly filled out before submitting.",
        );
        return;
      }

      const payload = editableWarehouses.map((wh) => ({
        id: wh.id,
        name: wh.name,
        location: wh.location,
        capacity: parseInt(wh.capacity),
        status: wh.status.toUpperCase(),
      }));

      axiosInstance
        .put(`${API_BASE}/warehouses/bulk`, payload)
        .then(() => {
          toast.success("Bulk update submitted successfully");
          setWarehouses([...editableWarehouses]);
          fetchWarehouses();
        })
        .catch((error) => {
          console.error("Error updating warehouses:", error);
        });
    }

    setBulkUpdateEnabled((prev) => !prev);
  };

  const handleBatchDelete = () => {
    if (selectedWarehouses.length === 0) {
      toast.warning("Please select at least one warehouse to delete");
      return;
    }

    setPopupAction(() => async () => {
      try {
        await Promise.all(
          selectedWarehouses.map((id) =>
            axiosInstance.delete(`${API_BASE}/warehouses/${id}`),
          ),
        );
        toast.success(
          `Successfully deleted ${selectedWarehouses.length} warehouse(s)`,
        );
        setSelectedWarehouses([]);
        setSelectAll(false);
        fetchWarehouses();
      } catch (error) {
        console.error("Error deleting warehouses:", error);
      } finally {
        setShowWarningPopup(false);
      }
    });
    setShowWarningPopup(true);
  };

  const toggleWarehouseStatus = async (id, enabled) => {
    try {
      await axiosInstance.put(
        `${API_BASE}/warehouses/${enabled ? "disable" : "enable"}/${id}`,
      );

      setWarehouses((prevWarehouses) =>
        prevWarehouses.map((wh) =>
          wh.id === id
            ? { ...wh, status: enabled ? "DISABLED" : "ENABLED" }
            : wh,
        ),
      );
      setEditableWarehouses((prevWarehouses) =>
        prevWarehouses.map((wh) =>
          wh.id === id
            ? { ...wh, status: enabled ? "DISABLED" : "ENABLED" }
            : wh,
        ),
      );

      toast.success("Warehouse status updated");
    } catch (error) {
      console.error("Error toggling warehouse status:", error);
    }
  };

  const handleDeleteWarehouse = (id) => {
    setPopupAction(() => async () => {
      try {
        await axiosInstance.delete(`${API_BASE}/warehouses/${id}`);
        toast.success("Warehouse deleted successfully");
        fetchWarehouses();
      } catch (error) {
        console.error("Error deleting warehouse:", error);
      } finally {
        setShowWarningPopup(false);
      }
    });
    setShowWarningPopup(true);
  };

  const handleEditWarehouse = (wh) => {
    setInitialValues(wh);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (isEditing) {
        await axiosInstance.put(`${API_BASE}/warehouses`, values);
        toast.success("Warehouse updated successfully");
      } else {
        await axiosInstance.post(`${API_BASE}/warehouses`, values);
        toast.success("Warehouse created successfully");
      }
      fetchWarehouses();
      markPageAsCompleted("Warehouse");
      resetForm();
      setShowForm(false);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving warehouse:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const tableColumns = [
    {
      header: (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <ColoredCheckbox
            name="select-all"
            color="green"
            checked={selectAll}
            onChange={handleSelectAll}
            value="all"
          />
          <span>Warehouse Name</span>
        </div>
      ),
      accessor: "name",
    },
    { header: "Location", accessor: "location" },
    { header: "Capacity", accessor: "capacity" },
    { header: "Status", accessor: "status" },
    { header: "Actions", accessor: "actions" },
    { header: "", accessor: "searchableText", hidden: true },
  ];

  const transformedData = (
    bulkUpdateEnabled ? editableWarehouses : warehouses
  ).map((wh) => ({
    name: (
      <div className="warehouse-name-cell">
        <ColoredCheckbox
          name={`warehouse-${wh.id}`}
          color="green"
          checked={selectedWarehouses.includes(wh.id)}
          onChange={() => handleSelectWarehouse(wh.id)}
          value={wh.id}
        />
        <span className="warehouse-avatar" aria-hidden="true">
          {(wh.name || "?").charAt(0).toUpperCase()}
        </span>
        {bulkUpdateEnabled ? (
          <input
            type="text"
            value={wh.name}
            onChange={(e) => handleInlineEdit(wh.id, "name", e.target.value)}
            className="inline-edit-input"
            style={{
              border: "1px solid #ccc",
              borderRadius: "8px",
              padding: "4px 8px",
              fontSize: "14px",
              minWidth: "120px",
            }}
          />
        ) : (
          <span className="warehouse-name-text">{wh.name}</span>
        )}
      </div>
    ),
    location: bulkUpdateEnabled ? (
      <input
        type="text"
        value={wh.location}
        onChange={(e) => handleInlineEdit(wh.id, "location", e.target.value)}
        className="inline-edit-input"
        style={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "4px 8px",
          fontSize: "14px",
          minWidth: "100px",
        }}
      />
    ) : (
      wh.location
    ),
    capacity: bulkUpdateEnabled ? (
      <input
        type="number"
        value={wh.capacity}
        onChange={(e) => handleInlineEdit(wh.id, "capacity", e.target.value)}
        className="inline-edit-input"
        style={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "4px 8px",
          fontSize: "14px",
          width: "80px",
        }}
        min="1"
      />
    ) : (
      wh.capacity
    ),
    status: (
      <div className="warehouse-status-cell">
        <ToggleButton
          isOn={wh.status === "ENABLED"}
          onToggle={() => toggleWarehouseStatus(wh.id, wh.status === "ENABLED")}
          status={wh.status === "DISABLED"}
        />
        <span
          className={`warehouse-status-caption ${
            wh.status === "ENABLED" ? "is-enabled" : "is-disabled"
          }`}
        >
          {wh.status === "ENABLED" ? "Available for use" : "Hidden from use"}
        </span>
      </div>
    ),
    actions: (
      <div style={{ display: "flex", gap: "8px" }}>
        <IconButton
          icon="iconamoon:edit-light"
          onClick={() => handleEditWarehouse(wh)}
          variant="edit"
          title="Edit Warehouse"
        />
        <IconButton
          icon="fluent:delete-28-regular"
          onClick={() => handleDeleteWarehouse(wh.id)}
          variant="delete"
          title="Delete Warehouse"
        />
      </div>
    ),
    searchableText: `${wh.name} ${wh.location} ${wh.capacity} ${wh.status}`,
  }));

  if (loading) return <LoadingScreen />;

  const hasAccess = user?.authorities?.some((role) =>
    allowedRoles.includes(role),
  );

  if (!hasAccess) {
    return <AccessDenied />;
  }

  return (
    <div className="user-role">
      {/* Metric summary tiles - derived from real warehouse data */}
      <div className="user-role__metrics-grid">
        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Total Warehouses</span>
            <span className="metric-tile__value">{warehouseStats.total}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--neutral">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 2 2 7l10 5 10-5-10-5Zm0 10L2 7v10l10 5 10-5V7l-10 5Z"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Active Warehouses</span>
            <span className="metric-tile__value">{warehouseStats.active}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--success">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m5 13 4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Disabled Warehouses</span>
            <span className="metric-tile__value">
              {warehouseStats.disabled}
            </span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--warning">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" d="m9 9 6 6M15 9l-6 6" />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Total Capacity</span>
            <span className="metric-tile__value">
              {warehouseStats.totalCapacity.toLocaleString()}
            </span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--info">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Helpful tip */}
      <div className="user-role__tip-banner">
        <span className="user-role__tip-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 18h6M10 22h4M12 2a6 6 0 0 0-4 10.5c.6.6 1 1.4 1 2.3V16h6v-1.2c0-.9.4-1.7 1-2.3A6 6 0 0 0 12 2Z"
            />
          </svg>
        </span>
        <p>
          <strong>Tip:</strong> Set capacity accurately so stock levels can be
          tracked reliably. Disabling a warehouse hides it from new stock
          assignments but keeps its existing records intact.
        </p>
      </div>

      <div className="user-role__actions">
        <button
          type="button"
          className={`action-card ${showForm ? "action-card--active" : ""}`}
          onClick={() => {
            if (showForm) {
              setShowForm(false);
            } else {
              setInitialValues({
                id: null,
                name: "",
                location: "",
                capacity: "",
                status: "ENABLED",
              });
              setShowForm(true);
              setIsEditing(false);
            }
          }}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={showForm ? "M6 18 18 6M6 6l12 12" : "M12 4v16m8-8H4"}
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {showForm ? "Close Warehouse Form" : "Create Warehouse"}
            </span>
            <span className="action-card-desc">
              {showForm ? "Hide the warehouse form" : "Add a new warehouse"}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={`action-card ${bulkUpdateEnabled ? "action-card--success" : ""}`}
          onClick={handleBulkUpdateClick}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {bulkUpdateEnabled ? "Submit Bulk Update" : "Enable Bulk Update"}
            </span>
            <span className="action-card-desc">
              {bulkUpdateEnabled
                ? "Save changes made to multiple warehouses"
                : "Edit multiple warehouses"}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={`action-card action-card--danger-outline ${
            selectedWarehouses.length === 0 ? "action-card--disabled" : ""
          }`}
          onClick={handleBatchDelete}
          disabled={selectedWarehouses.length === 0}
        >
          <span className="action-card-copy">
            <span className="action-card-title">
              Bulk Delete ({selectedWarehouses.length})
            </span>
            <span className="action-card-desc">
              Permanently delete all selected warehouses
            </span>
          </span>
        </button>
      </div>

      <div
        className={`user-role__layout ${
          isSidebarCollapsed ? "user-role__layout--collapsed" : ""
        }`}
      >
        <div className="user-role__main">
          {showForm && (
            <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={warehouseValidationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-2">
                      {isEditing ? "Edit Warehouse" : "Create New Warehouse"}
                    </h3>

                    <div className="user-role-form__grid">
                      <div className="userrole-form-group">
                        <label className="label">Warehouse Name</label>
                        <Field
                          type="text"
                          name="name"
                          className="form-input"
                          placeholder="Eg: Main Warehouse"
                        />
                        <div className="helper-placeholder">
                          <span className="helper-text">
                            Choose a name that's easy to recognize across sites.
                          </span>
                        </div>
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="userrole-form-group">
                        <label className="label">Location</label>
                        <Field
                          type="text"
                          name="location"
                          className="form-input"
                          placeholder="Eg: Bangalore, Chennai"
                        />
                        <div className="helper-placeholder">
                          <span className="helper-text">
                            City or address used to identify this warehouse.
                          </span>
                        </div>
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="location"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="userrole-form-group">
                        <label className="label">Capacity</label>
                        <Field
                          type="number"
                          name="capacity"
                          className="form-input"
                          placeholder="Storage capacity"
                          min="1"
                        />
                        <div className="helper-placeholder">
                          <span className="helper-text">
                            Maximum stock units this warehouse can hold.
                          </span>
                        </div>
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="capacity"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="userrole-form-group button-group">
                        <label className="label invisible">Submit</label>
                        <ButtonWithIcon
                          type="submit"
                          icon="tdesign:send"
                          text={
                            isEditing ? "Update Warehouse" : "Create Warehouse"
                          }
                          disabled={isSubmitting}
                          className={
                            isEditing ? "edit-button" : "submit-button"
                          }
                        />
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}

          <div className="user-role__table-section">
            {warehouseStats.total === 0 ? (
              <div className="user-role__empty-state">
                <svg
                  className="user-role__empty-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01"
                  />
                </svg>
                <h3>No warehouses found</h3>
                <p>
                  Warehouses are where your stock physically lives. To get
                  started:
                </p>
                <ol className="user-role__empty-steps">
                  <li>Create your first warehouse</li>
                  <li>Set its location and capacity</li>
                  <li>Enable it so stock can be assigned to it</li>
                </ol>
                <button
                  type="button"
                  className="action-card action-card--primary"
                  onClick={() => setShowForm(true)}
                >
                  <span className="action-card-copy">
                    <span className="action-card-title">Create Warehouse</span>
                    <span className="action-card-desc">
                      Add your first warehouse
                    </span>
                  </span>
                </button>
              </div>
            ) : (
              <GeneralTable
                title="Warehouses"
                columns={tableColumns}
                data={transformedData}
                selectedCount={selectedWarehouses.length}
                showPagination={true}
              />
            )}
          </div>
        </div>

        <button
          type="button"
          className="user-role__sidebar-toggle"
          onClick={toggleSidebar}
          aria-expanded={!isSidebarCollapsed}
          aria-label={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          title={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={isSidebarCollapsed ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"}
            />
          </svg>
        </button>

        <aside className="user-role__sidebar" aria-hidden={isSidebarCollapsed}>
          <div className="sidebar-card sidebar-card--info">
            <button
              type="button"
              className={`sidebar-card__header ${
                !isInfoCardCollapsed ? "is-expanded" : ""
              }`}
              onClick={toggleInfoCard}
              aria-expanded={!isInfoCardCollapsed}
            >
              <h4>How It Works</h4>
              <svg
                className={`sidebar-card__chevron ${
                  isInfoCardCollapsed ? "is-collapsed" : ""
                }`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m6 9 6 6 6-6"
                />
              </svg>
            </button>
            <div
              className={`sidebar-card__body ${
                isInfoCardCollapsed ? "is-collapsed" : ""
              }`}
            >
              <div className="sidebar-card__body-inner">
                <ol className="sidebar-steps">
                  <li>
                    <strong>Create Warehouse</strong>
                    <span>
                      Add a warehouse with a name, location and capacity.
                    </span>
                  </li>
                  <li>
                    <strong>Enable Warehouse</strong>
                    <span>Enable it so stock can be assigned there.</span>
                  </li>
                  <li>
                    <strong>Assign Stock</strong>
                    <span>
                      Stock levels are tracked against each warehouse.
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <div className="sidebar-card sidebar-card--success">
            <h4>Best Practices</h4>
            <ul className="sidebar-list">
              <li>Use clear, location-based warehouse names</li>
              <li>Keep capacity figures up to date</li>
              <li>Disable warehouses that are temporarily out of use</li>
              <li>Review and clean up unused warehouses regularly</li>
            </ul>
          </div>

          <div className="sidebar-card sidebar-card--warning">
            <h4>Important Note</h4>
            <p>
              Deleting a warehouse is permanent and cannot be undone. Stock
              records assigned to it may need to be reassigned.
            </p>
          </div>
        </aside>
      </div>

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={() => popupAction()}
        onDecline={() => setShowWarningPopup(false)}
        title="Delete Confirmation"
        message={
          selectedWarehouses.length > 1
            ? `Are you sure you want to delete ${selectedWarehouses.length} selected warehouses? This action is permanent and cannot be undone. Stock records assigned to them may need to be reassigned.`
            : "Are you sure you want to delete this warehouse? This action is permanent and cannot be undone. Stock records assigned to it may need to be reassigned."
        }
        acceptText="Yes, Delete"
        declineText="Cancel"
      />
    </div>
  );
};

export default Warehouse;
